/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useRef, useState, useEffect } from 'react'
import Link from 'next/link'
import BannerCard from '@/components/common/banner-card'
import CustomCarousel from '@/components/common/slick-carousel'
import Icons from '@/components/icons'
import useIsMobile from '@/hooks/useIsMobile'
import { useRouter } from 'next/router'
import AppPopUp from './AppPopUp'
import { lerpColor, useSwipeProgress } from '@/hooks/useSwiperProgess'
import MainBannerSkeleton from '@/shimmer/home/MainBannerSkeleton'

interface Props {
  banners: []
  onBannerChange?: (banner: any) => void
  isLoading: boolean
}

interface BannerProps {
  item: {
    videoLink: string
    isFreeSampleBanner: boolean
    isLeadCollectionBanner: boolean
    title: string
    deepLink: {
      web: string
    }
    image: string
    webImage: string
  }
}

function TopBanner (props: Props): JSX.Element {
  const isMobile = useIsMobile()
  const {
    banners,
    onBannerChange,
    isLoading
  } = props
  const router = useRouter()
  const [showPopup, setShowPopup] = useState(false)
  const isBannerVisibleRef = useRef(false)
  const hasTrackedInitialViewRef = useRef(false)
  const childRef = useRef<{ handlePhotoClick: () => void }>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const currentSlideRef = useRef<number>(0)
  const slickRef = useRef<any>(null)

  const { onBeforeChange } = useSwipeProgress(containerRef, (progress: number) => {
    const n: number = banners.length
    if (n === 0) return
    const current: number = currentSlideRef.current
    const target = progress > 0 ? (current + 1) % n : (current - 1 + n) % n
    const from = (banners[current] as any)?.topBarColour ?? '#ffffff'
    const to = (banners[target] as any)?.topBarColour ?? from

    const START = 0.0
    const raw = Math.abs(progress)
    const t = raw < START ? 0 : (raw - START) / (1 - START)

    document.documentElement.style.setProperty(
      '--header-bg',
      lerpColor(from, to, t)
    )
  }, banners.length)

  const trackPromotionEvent = (
    eventName: 'view_promotion' | 'select_promotion',
    item: any
  ) => {
    window.gtag?.('event', eventName, {
      promotion_id: item?.bannerId ?? '',
      promotion_name: item?.title ?? '',
      creative_name: item?.bannerType ?? '',
      location_id: item?.bannerLocation ?? ''
    })
  }

  useEffect(() => {
    const element = containerRef.current

    if (element === null) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting

        isBannerVisibleRef.current = visible

        if (visible) {
          slickRef.current?.slickPlay()

          if (
            banners?.length > 0 &&
            !hasTrackedInitialViewRef.current
          ) {
            trackPromotionEvent(
              'view_promotion',
              banners[currentSlideRef.current]
            )

            hasTrackedInitialViewRef.current = true
          }
        } else {
          slickRef.current?.slickPause()
        }
      },
      {
        threshold: 0.5
      }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [banners])

  const heroBanner = ({ item }: BannerProps) => (
    <div>
      <Link
        className={`${item?.videoLink !== '' ? 'relative' : ''}`}
        href={'#'}
        onClick={(e) => {
          e.preventDefault()

          trackPromotionEvent('select_promotion', item)

          if (item?.isLeadCollectionBanner) {
            // show lead collection pop-up
            setShowPopup(true)
            childRef.current?.handlePhotoClick()
          } else if (item?.isFreeSampleBanner) {
            router.push({
              pathname: '/shops',
              query: {
                filterCategoryId: 'FREESAMPLE',
                filterId: 'FREE_SAMPLE',
                scrollToTop: true
              }
            }).catch(() => {
            })
          } else if (item?.videoLink === '') {
            router.push(item?.deepLink?.web).catch(() => {
            })
          }
        }}
      >
        <BannerCard
          name={item?.title}
          image={isMobile ? item?.image : item?.webImage}
          cardStyle={
            {
              overflow: 'hidden'
            }
          }
        />
        {
          item?.videoLink !== '' && (
            <button
              type="button"
              className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
              onClick={() => {
                // Play video
              }}
            >
              <Icons
                name="playIcon"
                className=""
                width="60px"
                fill="#fff"
              />
            </button>
          )
        }
      </Link>
    </div>
  )

  if (isLoading && banners?.length === 0) {
    return (
      <MainBannerSkeleton/>
    )
  }

  return (
    <div className="sm:mx-0 mb-4" ref={containerRef} onTouchStart={() => slickRef.current?.slickPause()} // NEW
         onTouchEnd={() => slickRef.current?.slickPlay()} // NEW
         onTouchCancel={() => slickRef.current?.slickPlay()} // NEW
    >
      <CustomCarousel
        ref={slickRef}
        itemsPerCarousel={1}
        list={banners}
        SliderItem={heroBanner}
        idField="bannerId"
        interval={1000}
        showActiveDots
        arrows={false}
        autoPlay={true}
        otherSettings={{
          beforeChange: (current: number, next: number) => {
            onBeforeChange(current, next)
          },
          afterChange: (index: number) => {
            currentSlideRef.current = index

            if (isBannerVisibleRef.current) {
              trackPromotionEvent('view_promotion', banners[index])
            }

            if (banners[index] && (onBannerChange != null)) {
              const banner = banners[index] as {
                topBarColour?: string
                topBarTextColour?: string
              }

              if (
                (banner.topBarColour ?? '') !== '' ||
                (banner.topBarTextColour ?? '') !== ''
              ) {
                onBannerChange(banner)
              }
            }
          }
        }}
      />
      <AppPopUp showPopup={showPopup} setShowPopup={setShowPopup} ref={childRef}/>
    </div>
  )
}

export default TopBanner
